import { useEffect, useRef, useState } from 'react';

export type BaselineCapture = {
  /** Stable id, e.g. "baseline_2026_05_01". */
  id: string;
  /** Operator-visible label, e.g. "May 1 · healthy". */
  label: string;
  /** Capture moment. */
  at: Date;
  /** Free-form note from the engineer who captured this baseline. */
  note?: string;
};

export type BaselinePickerProps = {
  /** Available baseline captures for the active asset. */
  captures: BaselineCapture[];
  /** Currently selected baseline id, or null when none picked. */
  activeId: string | null;
  /** Commit a new selection. Pass null to clear (i.e. "no baseline"). */
  onSelect: (id: string | null) => void;
  /** Operator-initiated "capture current FFT as baseline" action.
   *  The component just calls the handler; persistence lives in the
   *  parent. */
  onCaptureNow?: () => void;
};

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

function relative(at: Date): string {
  const diffMs = at.getTime() - Date.now();
  const days = Math.round(diffMs / 86_400_000);
  if (Math.abs(days) >= 1) return RELATIVE.format(days, 'day');
  const hours = Math.round(diffMs / 3_600_000);
  if (Math.abs(hours) >= 1) return RELATIVE.format(hours, 'hour');
  return RELATIVE.format(Math.round(diffMs / 60_000), 'minute');
}

/**
 * Baseline picker chip. Renders as a compact dropdown showing the
 * active baseline label; click opens a popover with the available
 * captures + a "Capture current FFT" CTA. Picking commits via
 * `onSelect`; capturing fires `onCaptureNow` and leaves persistence
 * to the parent (typically localStorage until the sessions backend
 * ships).
 *
 * The picker is deliberately the same chip footprint as the asset
 * picker — keeps the selector bar visually consistent at the S5
 * spec's 56 px height.
 */
export function BaselinePicker({
  captures,
  activeId,
  onSelect,
  onCaptureNow,
}: BaselinePickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = captures.find((c) => c.id === activeId) ?? null;

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="group inline-flex items-center gap-sp-2 rounded-r-default border border-gray-300 bg-gray-50 px-sp-3 py-sp-1 text-left text-fs-meta transition-colors hover:border-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
        title="Change comparison baseline"
      >
        <span className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          Baseline
        </span>
        <span className="font-mono text-gray-900">
          {active ? active.label : 'none'}
        </span>
        <span className="text-gray-700" aria-hidden>
          ▾
        </span>
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label="Choose comparison baseline"
          className="absolute right-0 z-10 mt-sp-1 w-80 rounded-r-card border border-gray-300 bg-gray-50 p-sp-1 shadow-lg"
        >
          <button
            type="button"
            role="option"
            aria-selected={activeId == null}
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
            className={
              'flex w-full items-center justify-between rounded-r-default px-sp-3 py-sp-2 text-left text-fs-meta transition-colors focus:outline-none ' +
              (activeId == null
                ? 'bg-info-bg text-gray-900'
                : 'text-gray-900 hover:bg-gray-100')
            }
          >
            <span>No baseline (compare to nothing)</span>
          </button>
          {captures.length === 0 ? (
            <p className="px-sp-3 py-sp-2 text-fs-meta text-gray-700">
              No captures yet for this asset.
            </p>
          ) : (
            captures.map((c) => {
              const selected = c.id === activeId;
              return (
                <button
                  key={c.id}
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onSelect(c.id);
                    setOpen(false);
                  }}
                  className={
                    'flex w-full items-start justify-between gap-sp-3 rounded-r-default px-sp-3 py-sp-2 text-left transition-colors focus:outline-none ' +
                    (selected
                      ? 'bg-info-bg text-gray-900'
                      : 'text-gray-900 hover:bg-gray-100')
                  }
                >
                  <span className="min-w-0">
                    <span className="block truncate text-fs-body">
                      {c.label}
                    </span>
                    {c.note ? (
                      <span className="block truncate font-mono text-fs-meta text-gray-700">
                        {c.note}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 font-mono text-fs-meta text-gray-700">
                    {relative(c.at)}
                  </span>
                </button>
              );
            })
          )}
          {onCaptureNow ? (
            <div className="mt-sp-1 border-t border-gray-300 px-sp-3 py-sp-2">
              <button
                type="button"
                onClick={() => {
                  onCaptureNow();
                  setOpen(false);
                }}
                className="inline-flex items-center gap-sp-1 text-fs-meta font-medium text-info hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
              >
                + Capture current FFT as baseline
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
